import { create } from 'zustand';
import { MaleDogProposal, MatingProposalOptions } from '@/types/ai';
import { aiMatingService } from '@/services/ai/aiMatingService';

/**
 * AI交配提案ストアの状態型定義
 */
interface AIProposalState {
  // 状態
  /** 提案リスト */
  proposals: MaleDogProposal[];
  /** ローディング状態 */
  loading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** 警告メッセージ */
  warning: string | null;
  /** 最後に分析したメス犬のID */
  lastAnalyzedFemaleId: string | null;
  /** 最後に分析したメス犬の名前 */
  lastAnalyzedFemaleName: string | null;
  /** 分析日時 */
  analyzedAt: string | null;
  /** 免責事項 */
  disclaimer: string | null;

  // アクション
  /** 交配提案を取得 */
  fetchProposals: (
    femaleId: string,
    userId: string,
    options?: MatingProposalOptions
  ) => Promise<void>;
  /** 提案をクリア */
  clearProposals: () => void;
  /** エラーをセット */
  setError: (error: string | null) => void;
}

/**
 * AI交配提案ストア
 */
export const useAIProposalStore = create<AIProposalState>((set) => ({
  // 初期状態
  proposals: [],
  loading: false,
  error: null,
  warning: null,
  lastAnalyzedFemaleId: null,
  lastAnalyzedFemaleName: null,
  analyzedAt: null,
  disclaimer: null,

  /**
   * 交配提案を取得
   */
  fetchProposals: async (
    femaleId: string,
    userId: string,
    options?: MatingProposalOptions
  ) => {
    set({
      loading: true,
      error: null,
      warning: null,
    });

    try {
      const response = await aiMatingService.getProposals(
        femaleId,
        userId,
        options
      );

      if (response.success) {
        set({
          proposals: response.proposals,
          lastAnalyzedFemaleId: response.femaleId,
          lastAnalyzedFemaleName: response.femaleName,
          analyzedAt: response.analyzedAt,
          disclaimer: response.disclaimer,
          loading: false,
        });
      } else {
        set({
          proposals: [],
          error: response.error || 'AI分析に失敗しました',
          loading: false,
        });
      }
    } catch (error) {
      console.error('AI提案取得エラー:', error);
      set({
        proposals: [],
        error:
          error instanceof Error ? error.message : 'AI分析中にエラーが発生しました',
        loading: false,
      });
    }
  },

  /**
   * 提案をクリア
   */
  clearProposals: () => {
    set({
      proposals: [],
      error: null,
      warning: null,
      lastAnalyzedFemaleId: null,
      lastAnalyzedFemaleName: null,
      analyzedAt: null,
      disclaimer: null,
    });
  },

  /**
   * エラーをセット
   */
  setError: (error) => {
    set({ error });
  },
}));
